import loadable from '@loadable/component';
import * as promptUtils from 'stores/Prompt/Utils';
import cartStore from 'stores/CartStore';
import pageStore from 'stores/PageStore';
import previewStore from 'stores/PreviewStore';
import payloadStore from 'stores/PayloadStore';
import visitorStore from 'stores/Visitor/VisitorStore';
import promptDisplayCoordinator, {
  OPEN_SOURCE_COLLAPSED_REOPEN,
} from 'stores/Prompt/PromptDisplayCoordinator';
import constants from 'Constants';
import apiReady from './ready';
import * as api from 'Api';
import * as utils from 'Utils';


// Note:
// Can't use `utils.isProduction()` to check production mode. If you use that samples
// will always get included into assets for some unknown reasons.
let samples = {};
if (process.env.NODE_ENV !== 'production') {
  samples = require('./samples').default;
}

// `backend/common/capture.py` uses this handshake when it photographs a prompt preview. It must
// be set by the preview entry after React has committed the fully preloaded popup and its visual
// assets have settled; setting it from a component mount races every lazy child chunk.
const RENDER_COMPLETE_ATTRIBUTE = 'data-ov-render';
const RENDER_COMPLETE_VALUE = 'complete';

function nextPaint() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

function waitForImage(image) {
  const decode = () => (
    typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve()
  );

  if (image.complete) return decode();

  return new Promise((resolve) => {
    const done = () => {
      image.removeEventListener('load', done);
      image.removeEventListener('error', done);
      resolve();
    };
    image.addEventListener('load', done, { once: true });
    image.addEventListener('error', done, { once: true });
  }).then(decode);
}

async function waitForPreviewPaint(root) {
  // Prompt.componentDidMount injects selected Google-font stylesheets before React calls the
  // render callback below, so this observes the actual font set, not the document's old one.
  const fontsReady = document.fonts && document.fonts.ready
    ? document.fonts.ready.catch(() => {})
    : Promise.resolve();
  const imagesReady = Promise.all(
    Array.from(root.querySelectorAll('img')).map(waitForImage)
  );

  await Promise.all([fontsReady, imagesReady]);

  // The first frame applies the completed font/image layout; the second is the first frame on
  // which that layout is painted. These frames follow real readiness and are not a blind timer.
  await nextPaint();
  await nextPaint();
}

class PromptsLoader {

  constructor() {}

  openTriggerCandidates = async (prompts, options = {}) => {
    const uniquePrompts = [];
    (prompts || []).forEach((prompt) => {
      if (!prompt || uniquePrompts.includes(prompt)) return;
      uniquePrompts.push(prompt);
    });

    // A collapsed campaign is a pull re-entry. Resolve it first so it keeps its current flow,
    // bypasses cadence, and wins over an automatic candidate arriving in the same event turn.
    let opened = false;
    for (const prompt of uniquePrompts) {
      if (prompt.openSource !== OPEN_SOURCE_COLLAPSED_REOPEN) continue;
      if (await prompt.updateTriggered(true)) opened = true;
    }

    const automaticCandidates = uniquePrompts.filter((prompt) => prompt.openSource !== OPEN_SOURCE_COLLAPSED_REOPEN);
    if (automaticCandidates.length) {
      const automaticOpened = await promptDisplayCoordinator.openAutomatic(
        automaticCandidates,
        options,
      );
      opened = opened || automaticOpened;
    }

    return opened;
  };

  renderDOM = async () => {
    if (window.OnVoardPrompt['rendered']) return;

    // The display gate below is skipped in preview mode, for two reasons:
    //
    // 1. It reads `payloadStore.prompts`, which preview never populates - the preview payload is
    //    deserialized into `previewStore.prompt` instead. So in preview the array is empty, both
    //    flags are false, and this returned before rendering anything. The failure was silent and
    //    looked identical to a broken payload: the API call had already succeeded, and the
    //    `window.ONVOARD_PREVIEW['loaded'] = 1` line right after this runs regardless, because
    //    this returns normally rather than throwing.
    // 2. Even with prompts present, these are STOREFRONT targeting filters - page URL, visitor
    //    type, cart state. None of them are satisfiable on a preview URL. A caller that asked to
    //    preview one specific prompt has already decided it should be shown.
    if (!previewStore.active) {
      for (const prompt of payloadStore.prompts) {
        await prompt.updateEvaluateFiltersResult();
      }

      let hasPromptDisplay = payloadStore.prompts.some((prompt) => prompt.display);
      let hasLauncherDisplay = payloadStore.prompts.some((prompt) => prompt.displayLauncher);
      if (!hasPromptDisplay && !hasLauncherDisplay) return;
    }

    let React = await import('react');
    let ReactDOM = await import('react-dom');
    const root = document.querySelector("#ov_app_prompt");

    if (previewStore.active) {
      const { default: App, preloadPreviewPrompt } = await import('./app');
      await preloadPreviewPrompt();
      ReactDOM.render(<App />, root, async () => {
        await waitForPreviewPaint(root);
        document.documentElement.setAttribute(RENDER_COMPLETE_ATTRIBUTE, RENDER_COMPLETE_VALUE);
      });
    } else {
      const App = loadable(() => import('./app'));
      ReactDOM.render(<App />, root);
    }
    window.OnVoardPrompt['rendered'] = true;
  };

  updateSessionVisitorType = async () => {
    const cookieName = constants.COOKIE_PROMPT_SESSION_VISITOR_TYPE;
    let visitorType = utils.getCookie(cookieName);
    if (visitorType) return;

    let isReturning = utils.getCookie(constants.COOKIE_PROMPT_LAST_SEEN) ? true : false;
    visitorType = isReturning ? constants.PROMPT_VISITORS_TYPE_RETURNING : constants.PROMPT_VISITORS_TYPE_NEW;
    utils.setSessionCookie(cookieName, visitorType);
  };

  incrSessionNumViewedPages = async () => {
    if (window.OnVoardPrompt['incr_session_num_viewed_pages']) return;

    const cookieName = constants.COOKIE_PROMPT_SESSION_NUM_VIEWED_PAGES;
    let numViewedPages = parseInt(utils.getCookie(cookieName) || 0);
    numViewedPages += 1;

    utils.setSessionCookie(cookieName, numViewedPages);
    window.OnVoardPrompt['incr_session_num_viewed_pages'] = true;
  };

  updateSessionFirstSeen = async () => {
    const cookieName = constants.COOKIE_PROMPT_SESSION_FIRST_SEEN;
    let ts = utils.getCookie(cookieName);
    if (ts) return;

    const value = utils.getCurrentTimestamp();
    utils.setSessionCookie(cookieName, value);
  };

  updateLastSeenCookie = async () => {
    const cookieName = constants.COOKIE_PROMPT_LAST_SEEN;
    const value = utils.getCurrentTimestamp();
    utils.setCookie(cookieName, value);
  };

  onScroll = async () => {
    let scrollPct = utils.getScrollPercent();
    pageStore.updateScrollPct(scrollPct);

    const candidates = [];
    for (const prompt of payloadStore.prompts) {
      if (prompt.triggered) continue;

      // scroll percentage trigger
      if (prompt.useScrollPercentageTrigger) {
        if (pageStore.scrollPct >= prompt.minScrollPctThreshold) {
          candidates.push(prompt);
        }
      }

      // mobile exit intent trigger
      if (prompt.useExitIntentTrigger && utils.isMobileScreen()) {
        if (pageStore.canTriggerMobileExitIntent) {
          candidates.push(prompt);
        }
      }
    }

    if (await this.openTriggerCandidates(candidates, {preserveState: true})) this.renderDOM();
  };

  onCartUpdate = async (evt) => {
    let cartData = utils.formatCartResponse(evt.detail);
    await cartStore.deserialize(cartData);
    pageStore.refreshPredefinedProperties();

    for (const prompt of payloadStore.prompts) {
      await prompt.updateEvaluateFiltersResult();
    }
  };

  onMouseLeave = async (evt) => {
    const candidates = [];

    for (const prompt of payloadStore.prompts) {
      if (prompt.triggered) continue;
      if (!prompt.useExitIntentTrigger) continue;
      candidates.push(prompt);
    }

    if (await this.openTriggerCandidates(candidates, {preserveState: true})) this.renderDOM();
  };

  onAddCartItem = async (evt) => {
    await cartStore.deserializeAddedItem(utils.formatCartItem(evt.detail));
    const candidates = payloadStore.prompts.filter((prompt) => prompt.useAddCartItemTrigger);

    // Can trigger multiple times per page, subject to the shared automatic session slot.
    if (await this.openTriggerCandidates(candidates, {preserveState: false})) this.renderDOM();
  };

  onJavascriptEvent = async (evt) => {
    const candidates = [];

    for (const prompt of payloadStore.prompts) {
      const settings = prompt.settings;

      for (const trigger of settings.triggers) {
        if (!trigger.isTypeJavascriptEvent) continue;
        if (trigger.javascriptEvent.keys.includes(evt.type)) {
          candidates.push(prompt);
          break;
        }
      }
    }

    if (await this.openTriggerCandidates(candidates, {preserveState: false})) this.renderDOM();
  };

  onLinkClick = async (evt) => {
    let target = evt.target;
    let el = target.closest("a");
    if (!el) return;

    let href = el.getAttribute("href");
    if (!href) return;

    const candidates = payloadStore.prompts.filter((prompt) => (
      href.includes(`#ov-prompt-${prompt.id}`)
    ));
    const opened = await this.openTriggerCandidates(candidates, {preserveState: false});
    if (opened) evt.preventDefault();

    if (opened) this.renderDOM();
  };

  onDetectClickTrigger = async (evt) => {
    await promptUtils.onDetectClickTrigger(evt, payloadStore, pageStore);
    this.renderDOM();
  };

  onInitiateCheckoutClick = async (evt) => {
    await promptUtils.onInitiateCheckoutClick(evt, payloadStore, pageStore);
    this.renderDOM();
  };

  cleanupEventListeners = () => {
    document.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('ov_cart_update', this.onCartUpdate);
    document.removeEventListener('mouseleave', this.onMouseLeave);
    document.removeEventListener("ov_add_cart_item", this.onAddCartItem);
    document.removeEventListener('click', this.onLinkClick);
    document.removeEventListener('click', this.onDetectClickTrigger);
    document.removeEventListener('click', this.onInitiateCheckoutClick);

    // javascript event trigger
    for (const prompt of payloadStore.prompts) {
      const settings = prompt.settings;
      for (const trigger of settings.triggers) {
        if (!trigger.isTypeJavascriptEvent) continue;
        for (const key of trigger.javascriptEvent.keys) {
          document.removeEventListener(key, this.onJavascriptEvent);
        }
      }
    }
  };

  setupEventListeners = () => {
    document.addEventListener('scroll', this.onScroll);
    document.addEventListener("ov_cart_update", this.onCartUpdate);
    document.addEventListener('mouseleave', this.onMouseLeave);
    document.addEventListener("ov_add_cart_item", this.onAddCartItem);
    document.addEventListener('click', this.onLinkClick);
    document.addEventListener('click', this.onDetectClickTrigger);
    document.addEventListener('click', this.onInitiateCheckoutClick);

    if (!window.OnVoardAddedScripts.promptIncrSecs) {
      window.OnVoardAddedScripts.promptIncrSecs = true;

      this.incrSecsHandler = utils.requestInterval(async () => {
        pageStore.incrSecsAfterLoad();
        const candidates = [];

        for (const prompt of payloadStore.prompts) {
          if (prompt.triggered) continue;
          if (!prompt.usePageLoadTrigger) continue;
          if (prompt.minPageLoadSecsThreshold > pageStore.secsAfterLoad) continue;
          candidates.push(prompt);
        }

        if (await this.openTriggerCandidates(candidates, {preserveState: true})) this.renderDOM();
      }, 1000*1);
    }
  };

  onReadyTasks = async () => {
    const setupTrigger = (prompt, trigger) => {
      const settings = prompt.settings;
      if (trigger.isTypePageLoad) {
        prompt.setUsePageLoadTrigger(true);
        if (!prompt.minPageLoadSecsThreshold || prompt.minPageLoadSecsThreshold > trigger.pageLoad.delaySecs) {
          prompt.setMinPageLoadSecsThreshold(trigger.pageLoad.delaySecs);
        }
      }

      if (trigger.isTypeExitIntent) {
        prompt.setUseExitIntentTrigger(true);
      }

      if (trigger.isTypeScrollPercentage) {
        prompt.setUseScrollPercentageTrigger(true);
        if (!prompt.minScrollPctThreshold || prompt.minScrollPctThreshold > trigger.scrollPercentage.pct) {
          prompt.setMinScrollPctThreshold(trigger.scrollPercentage.pct);
        }
      }

      if (trigger.isTypeClick) {
        prompt.setUseClickTrigger(true);
      }

      if (trigger.isTypeJavascriptEvent) {
        prompt.setUseJavascriptEventTrigger(true);
        trigger.javascriptEvent.keys.forEach((key) => {
          document.addEventListener(key, this.onJavascriptEvent);
        })
      }

      if (trigger.isTypeAddCartItem) {
        prompt.setUseAddCartItemTrigger(true);
      }

      if (trigger.isTypeInitiateCheckout && pageStore.isSupportedEcommerePlatform) {
        prompt.setUseInitiateCheckoutTrigger(true);
        if (pageStore.isWix) promptUtils.handleWixInitiateCheckout(prompt, trigger, pageStore);
      }
    };

    payloadStore.prompts.forEach((prompt) => {
      const settings = prompt.settings;
      settings.triggers.forEach((trigger) => {
        try {
          setupTrigger(prompt, trigger);
        } catch (error) {
          console.error(error);
        }
      })
    })
  };

  loadPrompts = async () => {
    const el = document.querySelector(".ov-app-prompt");

    // Preview is checked BEFORE the dev sample fallback. It used to be the other way round, which
    // made the preview branch unreachable in every non-production build: the console's preview
    // page would set window.ONVOARD_PREVIEW, this would ignore it, render the hardcoded s1 sample
    // instead, and never call the preview API at all. A caller that explicitly asked to preview a
    // specific prompt has said what it wants; the sample is only a fallback for when nothing did.
    // This is also why server-side screenshot capture could not work outside production.
    if (previewStore.active) {
      const successCallback = async (response) => {
        if (response.prompt && response.prompt.status === constants.PROMPT_STATUS_DRAFT) previewStore.setIsDraft(true);
        await previewStore.deserialize(response);

        this.renderDOM();
        window.ONVOARD_PREVIEW['loaded'] = 1;
      };

      const errorCallback = (errors) => {
        console.error(errors);
      };

      const params = {};
      params['token'] = previewStore.token;
      api.getPromptPreview(successCallback, errorCallback, previewStore.promptId, params);
      return;
    }

    if (!utils.isProduction()) {
      await payloadStore.deserialize(samples.s1);

      apiReady();
      await this.onReadyTasks();
      this.renderDOM();
      return;
    }

    // require account ID below
    const ovData = window.OnVoardData;
    const platform = utils.getEcommercePlatform();

    let accountId = el.getAttribute('data-account-id');
    if (!accountId && platform === 'shopline') {
      if (ovData && ovData.config) {
        accountId = ovData.config['account_id'];
      }
    }

    if (accountId && !el.getAttribute('data-account-id')) {
      el.setAttribute("data-account-id", accountId);
    }

    if (!accountId) return;

    const successCallback = async (response) => {
      await payloadStore.deserialize(response);
      if (response.visitor) await visitorStore.deserialize(response.visitor);

      apiReady();
      await this.onReadyTasks();
      this.renderDOM();
    };

    const errorCallback = (errors) => {
      utils.printErrors(errors);
    };

    const params = {};
    params['pageview_id'] = window.OnVoardPrompt['pageview_id'];
    const contactId = utils.getContactId();
    const cookieId = utils.getCookieId();
    const visitorId = utils.getPromptVisitorId();
    if (contactId) params['contact_id'] = contactId;
    if (cookieId) params['cookie_id'] = cookieId;
    if (visitorId) params['visitor_id'] = visitorId;

    api.getAccountPromptPayloads(successCallback, errorCallback, accountId, params);
  };

  resetStores = () => {
    promptDisplayCoordinator.reset();
    cartStore.reset();
    pageStore.reset();
    previewStore.reset();
    payloadStore.reset();
    visitorStore.reset();
  };

  start = async () => {
    this.resetStores();
    window.OnVoardPrompt = {
      'rendered': false,
      'viewed_prompts': [],
      'engaged_prompts': [],
      'closed_prompts': [],
      'pageview_id': utils.uniqueId(),
      'tracked_recommender_impressions': [],
    };

    // prompt preview
    if (window.ONVOARD_PREVIEW) {
      document.documentElement.removeAttribute(RENDER_COMPLETE_ATTRIBUTE);
      previewStore.setActive(true);
      previewStore.setPromptId(window.ONVOARD_PREVIEW['prompt_id']);
      previewStore.setToken(window.ONVOARD_PREVIEW['token']);
      previewStore.setEntityId(window.ONVOARD_PREVIEW['entity_id']);
      await this.loadPrompts();
      return;
    }

    utils.setupCartUpdateEvent();

    // need cleanup to avoid duplicates
    this.cleanupEventListeners();
    this.setupEventListeners();

    this.updateSessionVisitorType();
    this.incrSessionNumViewedPages();
    this.updateSessionFirstSeen();
    this.updateLastSeenCookie();
    pageStore.refreshEcommercePlatform();
    pageStore.refreshPredefinedProperties();

    await this.loadPrompts();

    // load cart data
    utils.loadInitialCartData();
  };
}

export { PromptsLoader };

export function load() {
  let loader = new PromptsLoader();
  loader.start();
}
